import React from 'react';
import Button from '../Button/Button';
// @ts-ignore
import checkIcon from '../../image-assets/icon.checkmark.svg';
import './ContentCard.css';

interface ContentCardProps {
  title: string;
  subtitle: string;
  buttonText: string;
  onButtonClick: () => void;
  totalKeys: number;
  isComplete: boolean;
  isLoading?: boolean;
  isPreparing?: boolean;
  onCancel?: () => void;
  progress?: {
    completed_items: number;
    total_items: number;
    progress_percentage: number;
  };
}

const ContentCard: React.FC<ContentCardProps> = ({
  title,
  subtitle,
  buttonText,
  onButtonClick,
  totalKeys,
  isComplete,
  isLoading = false,
  isPreparing = false,
  onCancel,
  progress
}) => {
  // Extract language name from title for translating state
  const getTitle = () => {
    if (isComplete) {
      // Extract language name from title (e.g., "Translate Bulgarian" -> "Bulgarian")
      const languageMatch = title.match(/Translate (.+)/);
      const languageName = languageMatch ? languageMatch[1] : 'Language';
      return `${languageName} translations complete`;
    }
    if (isPreparing) {
      // Extract language name from title (e.g., "Translate Bulgarian" -> "Bulgarian")
      const languageMatch = title.match(/Translate (.+)/);
      const languageName = languageMatch ? languageMatch[1] : 'Language';
      return `Preparing ${languageName} translation...`;
    }
    if (isLoading) {
      // Extract language name from title (e.g., "Translate Bulgarian" -> "Bulgarian")
      const languageMatch = title.match(/Translate (.+)/);
      const languageName = languageMatch ? languageMatch[1] : 'Language';
      return `Translating to ${languageName}`;
    }
    return title;
  };

  // Calculate progress percentage for the background layer
  const getProgressPercentage = () => {
    if (isPreparing) return 0;
    if (!progress || !progress.total_items || progress.total_items === 0) return 0;
    return Math.round(((progress.completed_items || 0) / progress.total_items) * 100);
  };

  // Check if progress is complete
  const isProgressComplete = getProgressPercentage() === 100;

  return (
    <div 
      className={`content-card ${isComplete ? 'success' : ''} ${(isLoading || isPreparing) ? 'active' : ''} ${isPreparing ? 'preparing' : ''} ${isProgressComplete ? 'complete' : ''}`}
      style={(isLoading || isPreparing) ? {
        '--progress-width': `${getProgressPercentage()}%`
      } as React.CSSProperties : undefined}
    >
      <div className="content-card-text">
        <div className="content-card-title">
          {getTitle()}
        </div>
      </div>
      {isComplete ? (
        <img src={checkIcon} alt="Complete" className="content-card-icon" />
      ) : (
        <div className="content-card-actions">
          {isPreparing ? (
            <>
              <div className="content-card-progress-container">
                <span className="content-card-progress-text">
                  Preparing...
                </span>
              </div>
              {onCancel && (
                <Button
                  onClick={onCancel}
                  variant="secondary"
                  size="medium"
                >
                  Cancel
                </Button>
              )}
            </>
          ) : isLoading ? (
            <>
              <div className="content-card-progress-container">
                <span className="content-card-progress-text">
                  Translating {progress?.completed_items || 0} of {progress?.total_items || 0}
                </span>
              </div>
              {onCancel && (
                <Button
                  onClick={onCancel}
                  variant="secondary"
                  size="medium"
                >
                  Cancel
                </Button>
              )}
            </>
          ) : (
            <Button
              onClick={onButtonClick}
              disabled={isComplete}
            >
              {buttonText}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentCard; 